import {
  Component,
  forwardRef,
  Inject,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidatorFn
} from '@angular/forms';
import {
  AgpFormUtil,
  CvaFormGroupComponent,
  ResetOptions
} from '@liberty-financial/agp-shared';
import {
  LDS_CONFIG,
  LdsConfig,
  TELEPHONE_DEFAULT_MIN_LENGTH,
  TELEPHONE_DEFAULT_MAX_LENGTH,
  TELEPHONE_DEFAULT_PATTERN,
  TelephoneService,
  TelephoneType,
  TelephoneTypes
} from '@liberty-financial/ng-core';

const TELEPHONE_AU_MIN_LENGTH = 6;

@Component({
  selector: 'spark-shared-contact-number',
  templateUrl: './contact-number.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SparkSharedContactNumberComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SparkSharedContactNumberComponent),
      multi: true
    }
  ]
})
export class SparkSharedContactNumberComponent
  extends CvaFormGroupComponent<string | null>
  implements OnInit, OnChanges
{
  @Input()
  label: string = '';

  @Input()
  countryCode: string = this.config.countryCode; // otherwise use config token

  @Input()
  isRequired: boolean = false;

  @Input()
  showHighlight: boolean = false;

  @Input()
  validations: { [index: string]: string } = {};

  public form = new FormControl<string | null>(null);
  public formGroup = new FormGroup({
    contactNumber: this.form
  });

  private _allowedTypes: Array<TelephoneTypes> = [
    TelephoneType.Landline,
    TelephoneType.Mobile
  ];
  public placeholder: string = '';
  public minLength: number = TELEPHONE_DEFAULT_MIN_LENGTH;
  public maxLength: number = TELEPHONE_DEFAULT_MAX_LENGTH;

  constructor(
    @Inject(LDS_CONFIG) private config: LdsConfig,
    private telephoneService: TelephoneService
  ) {
    super();
  }

  ngOnInit(): void {
    this.assignProperties(this.countryCode);
    this.assignValidators(this.countryCode);
  }

  ngOnChanges(changes: SimpleChanges) {
    // required
    if (changes.isRequired) {
      AgpFormUtil.Validators.updateRequiredIfNecessary(
        this.form,
        changes.isRequired.currentValue
      );
    }

    // country code
    if (changes.countryCode) {
      this.assignProperties(changes.countryCode.currentValue);
      this.assignValidators(changes.countryCode.currentValue);
    }
  }

  private assignProperties(countryCode: string): void {
    this.placeholder = this.determinePlaceholder(countryCode);
    this.minLength = this.determineMinLength(countryCode);
    this.maxLength = this.determineMaxLength(countryCode);
  }

  private assignValidators(countryCode: string): void {
    const pattern = this.determinePattern(countryCode);
    const contactValidator = this.contactNumberValidator(pattern);

    if (!this.form.hasValidator(contactValidator)) {
      this.form.addValidators(contactValidator);
      this.form.updateValueAndValidity();
    }
  }

  protected reset(opts?: ResetOptions | undefined): void {
    this.form.reset(undefined, opts);
  }

  onPasteDigits(value: string): void {
    this.form.setValue(value);
  }

  private getLengths(countryCode: string): Array<number> {
    const lengths: Array<number> = [];
    this._allowedTypes.forEach((type) => {
      const length = this.telephoneService.getLengths(countryCode, type);

      if (length) {
        lengths.push(...length);
      }
    });

    return lengths;
  }

  private getPatterns(countryCode: string): Array<RegExp> {
    const patterns: Array<RegExp> = [];
    this._allowedTypes.forEach((type) => {
      const pattern = this.telephoneService.getPattern(countryCode, type);

      if (pattern) {
        patterns.push(pattern);
      }
    });

    return patterns;
  }

  private combinePatterns(patterns: Array<RegExp>): RegExp {
    return new RegExp(patterns.map((pattern) => pattern.source).join('|'));
  }

  private determinePlaceholder(countryCode: string): string {
    return (
      this.telephoneService.getExample(countryCode, TelephoneType.Landline) ||
      ''
    );
  }

  private determineMinLength(countryCode: string): number {
    if (countryCode === 'AU') {
      return TELEPHONE_AU_MIN_LENGTH;
    }
    const lengths = this.getLengths(countryCode);
    return lengths.length ? Math.min(...lengths) : TELEPHONE_DEFAULT_MIN_LENGTH;
  }

  private determineMaxLength(countryCode: string): number {
    const lengths = this.getLengths(countryCode);
    return lengths.length ? Math.max(...lengths) : TELEPHONE_DEFAULT_MAX_LENGTH;
  }

  private determinePattern(countryCode: string): RegExp {
    if (countryCode === 'AU') {
      return RegExp('^(13\\d{4}|(1300|1800)\\d{6}|\\d{7,12})$'); // e.g. 131234, 1300123456, 1800123456, 0312345678
    }
    const patterns = this.getPatterns(countryCode);
    return patterns.length
      ? this.combinePatterns(patterns)
      : TELEPHONE_DEFAULT_PATTERN;
  }

  private contactNumberValidator(pattern: RegExp): ValidatorFn {
    return (control: AbstractControl): { [key: string]: string } | null => {
      const controlValue = control.value;

      if (!controlValue || controlValue === undefined) {
        return null;
      }

      const isValid = pattern.test(controlValue);

      return isValid
        ? null
        : { contactNumberValidator: 'Contact number is invalid' };
    };
  }
}
